import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import Lightbox from '../components/Lightbox';
import { MENUS } from '../data/menus';
import { MENU_PAGES } from '../data/media';
import './Menus.css';

/* Mirrors wethelocal.co/menu/: a centred hand-lettered "Menus" title with an
   "AT THE LOCAL" eyebrow, three tabs (Food / Drinks / Golden hour), and the
   scanned menu pages stacked full-width beneath — nothing else. */

const SHEETS = {
  food: MENU_PAGES.food,
  drinks: MENU_PAGES.drinks,
  'golden-hour': MENU_PAGES.goldenHour,
};

function Sheets({ pages, menuName }) {
  const [open, setOpen] = useState(null);
  if (!pages?.length) return null;

  return (
    <>
      <ul className="sheets">
        {pages.map((page, i) => (
          <Reveal as="li" className="sheets__cell" key={page.src} delay={Math.min(i, 2) * 0.06}>
            <button
              type="button"
              className="sheet"
              onClick={() => setOpen(i)}
              aria-label={`Open ${menuName} menu, page ${i + 1} of ${pages.length}, full size`}
            >
              <img src={page.src} alt={page.alt} loading={i < 2 ? 'eager' : 'lazy'} />
            </button>
          </Reveal>
        ))}
      </ul>

      <Lightbox items={pages} index={open} onClose={() => setOpen(null)} onIndex={setOpen} />
    </>
  );
}

export default function Menus() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('m');
  const valid = MENUS.some((x) => x.id === requested);
  const [active, setActive] = useState(valid ? requested : MENUS[0].id);

  useEffect(() => {
    if (valid && requested !== active) setActive(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);

  const select = (id) => {
    setActive(id);
    setParams(id === MENUS[0].id ? {} : { m: id }, { replace: true });
  };

  const menu = MENUS.find((x) => x.id === active) || MENUS[0];
  const pages = SHEETS[menu.id] || [];

  return (
    <>
      <Seo
        title={`${menu.name} menu`}
        description="Food, drinks and golden hour at The Local — the everyday Ugandan table, reimagined with creativity and care. Bugolobi and Kyanja, Kampala."
        path="/menus"
      />

      <section className="menuhead">
        <div className="page">
          <Reveal as="h1" className="display brand menuhead__title">
            Menus
          </Reveal>
          <Reveal as="p" className="menuhead__eyebrow" delay={0.08}>
            At The Local
          </Reveal>
        </div>
      </section>

      <div className="menutabs">
        <div className="page menutabs__inner" role="tablist" aria-label="Menus">
          {MENUS.map((x) => (
            <button
              key={x.id}
              type="button"
              role="tab"
              id={`tab-${x.id}`}
              aria-selected={active === x.id}
              aria-controls={`panel-${x.id}`}
              className={`menutabs__tab${active === x.id ? ' is-active' : ''}`}
              onClick={() => select(x.id)}
            >
              {x.name}
            </button>
          ))}
        </div>
      </div>

      <section
        className="section section--tight menupanel"
        role="tabpanel"
        id={`panel-${menu.id}`}
        aria-labelledby={`tab-${menu.id}`}
      >
        <div className="page">
          <Sheets pages={pages} menuName={menu.name} />
        </div>
      </section>
    </>
  );
}
